// NotificationList — shared список (используется в Panel, Sheet, /notifications route).
//
// Тактильный редизайн «Картотека» (§8.V): уведомления сгруппированы по
// временным секциям (Сегодня / Вчера / …) с заголовками-разделителями;
// строки — тактильные карточки e1; непрочитанные различимы акцент-маркером.
// Пусто / загрузка / ошибка — единые состояния «Картотека».
//
// Внутри секции однотипные уведомления схлопываются в раскрывающуюся карточку
// (`AggregatedGroup`): три артефакта по одному материалу — одна строка со
// счётчиком, а не три. Правило схлопывания — `aggregate-notifications` в
// `@studyhub/shared` (общее с сервером, окно 30 мин по `(type, groupKey)`).
//
// >50 items — @tanstack/react-virtual (flattened group rows).
//
// Mutations (markRead / markAllRead / delete) идут через PowerSync `db.execute(SQL)`
// — таблица `notifications` синкаемая, write-handler сервера принимает PATCH
// (readAt) + DELETE. tRPC mutations дублировались бы (rules/offline-sync.md §13, §18).
//
// updatePreferences остаётся на tRPC — пишет audit log (см. modules/notifications/
// mutations/update-preferences.ts).

import { Button } from "@studyhub/ui/components/button";
import { EmptyState, LoadingState } from "@studyhub/ui/components/empty-state";
import { ErrorState } from "@studyhub/ui/components/error-state";
import { ScrollArea } from "@studyhub/ui/components/scroll-area";
import { toast } from "@studyhub/ui/toast";
import { IconBell } from "@tabler/icons-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NOTIFICATION_VIRTUAL_THRESHOLD } from "@/lib/list-performance";
import { connectPowerSync, getPowerSync } from "@/lib/powersync/client";
import { AggregatedGroup } from "./aggregated-group";
import { isNotificationHiddenOnSurface } from "./app-hidden-types";
import { type ContentRow, clusterize, countNotifications } from "./clusterize";
import { FilterBar, type NotificationFilter } from "./filter-bar";
import { type DateGroupKey, groupByDate } from "./group-by-date";
import { NotificationItem, type NotificationItemData } from "./notification-item";

interface NotificationListProps {
	items: NotificationItemData[];
	isLoading?: boolean;
	isError?: boolean;
	/** Повторная попытка при ошибке. Если не задан — fallback на reconnect PowerSync. */
	onRetry?: () => void;
	/** Вызывается при клике по уведомлению (mobile Sheet → закрыть панель). */
	onItemSelect?: () => void;
	/** Demo: ?demo=loading|error|empty */
	demo?: "loading" | "error" | "empty";
}

type VirtualRow = { kind: "header"; key: DateGroupKey; count: number } | ContentRow;

export function NotificationList({
	items,
	isLoading,
	isError,
	onRetry,
	onItemSelect,
	demo,
}: NotificationListProps) {
	const { t } = useTranslation("notifications");
	// Real-time: новые уведомления синкаются через PowerSync `user_notifications`
	// stream → `usePowerSyncQuery` выше по дереву рендерит автоматически.

	const [filter, setFilter] = useState<NotificationFilter>("all");
	const [optimisticReads, setOptimisticReads] = useState<Set<string>>(new Set());
	const scrollRef = useRef<HTMLDivElement>(null);

	const filteredItems = useMemo(() => {
		let result = items.map((i) => (optimisticReads.has(i.id) ? { ...i, read: true } : i));
		if (filter !== "all") {
			result = result.filter((i) => matchFilter(i.type, filter));
		}
		return result;
	}, [items, filter, optimisticReads]);

	const groups = useMemo(() => groupByDate(filteredItems), [filteredItems]);

	/** Секции с уже схлопнутыми сериями — общий источник для обеих веток рендера. */
	const sections = useMemo(
		() => groups.map((group) => ({ key: group.key, rows: clusterize(group.items) })),
		[groups],
	);

	const virtualRows = useMemo<VirtualRow[]>(() => {
		const rows: VirtualRow[] = [];
		for (const section of sections) {
			// Счётчик в заголовке — по уведомлениям, а не по строкам: свёрнутая
			// группа из трёх не должна превращать «Сегодня 3» в «Сегодня 1».
			rows.push({ kind: "header", key: section.key, count: countNotifications(section.rows) });
			rows.push(...section.rows);
		}
		return rows;
	}, [sections]);

	const useVirtual = filteredItems.length > NOTIFICATION_VIRTUAL_THRESHOLD;

	const virtualizer = useVirtualizer({
		count: virtualRows.length,
		// Скроллит НЕ этот div, а viewport внутри Radix `ScrollArea`: нативный
		// `overflow-y-auto` вернул системный скроллбар в панель 400px (на
		// Windows/Linux он видим всегда и съедает её ширину) — правило
		// frontend-ui.md §9.3. Radix ref'а на viewport не отдаёт, поэтому берём
		// его дата-атрибутом — тот же приём, что в `tutor-panel-history`.
		getScrollElement: () =>
			scrollRef.current?.querySelector<HTMLElement>("[data-radix-scroll-area-viewport]") ?? null,
		estimateSize: (index) => (virtualRows[index]?.kind === "header" ? 28 : 88),
		overscan: 8,
		enabled: useVirtual,
	});

	const onMarkRead = async (id: string): Promise<void> => {
		setOptimisticReads((prev) => new Set(prev).add(id));
		try {
			await getPowerSync().execute(
				`UPDATE notifications SET read_at = ? WHERE id = ? AND read_at IS NULL`,
				[new Date().toISOString(), id],
			);
		} catch (_err) {
			setOptimisticReads((prev) => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
			toast.error(t("errorTitle"));
		}
	};

	const onDelete = async (id: string): Promise<void> => {
		try {
			await getPowerSync().execute(`DELETE FROM notifications WHERE id = ?`, [id]);
			toast.success(t("toast.deleted"));
		} catch (_err) {
			toast.error(t("errorTitle"));
		}
	};

	const showLoading = isLoading || demo === "loading";
	const showError = isError || demo === "error";
	const showEmpty = demo === "empty" || (!showLoading && !showError && filteredItems.length === 0);

	return (
		<div className="flex h-full flex-col">
			<div className="shrink-0 border-b border-border bg-muted/30 px-3 py-3">
				<FilterBar value={filter} onValueChange={setFilter} />
			</div>

			{/* `min-h-0 flex-1` обязательны: viewport у Radix — `size-full`, и без
			    definite height у Root скролл не появится (см. `scroll-area/index.tsx`).
			    Внутреннюю `display:table`-обёртку viewport'а пакет уже форсит в
			    `block`, поэтому абсолютные строки виртуализации в ней не схлопываются. */}
			{/* `overscroll-contain` — на viewport'е, а не на Root: скроллит именно
			    viewport, и на Root правило ни на что не влияет. Домотав список в
			    шторке до конца, на iOS/Android можно было утащить страницу под
			    шторкой. */}
			<ScrollArea
				ref={scrollRef}
				className="min-h-0 flex-1 [&>[data-radix-scroll-area-viewport]]:overscroll-contain"
			>
				<div className="p-3">
					{showLoading && <LoadingState rows={5} />}

					{showError && (
						<ErrorState
							title={t("errorTitle")}
							action={
								<Button
									size="sm"
									variant="outline"
									className="cursor-pointer"
									onClick={() => {
										if (onRetry) onRetry();
										else void connectPowerSync();
									}}
								>
									{t("retry")}
								</Button>
							}
						/>
					)}

					{showEmpty && (
						<EmptyState icon={<IconBell />} title={t("empty")} description={t("emptyHint")} />
					)}

					{!showLoading && !showError && !showEmpty && useVirtual ? (
						<div className="relative w-full" style={{ height: `${virtualizer.getTotalSize()}px` }}>
							{virtualizer.getVirtualItems().map((virtualRow) => {
								const row = virtualRows[virtualRow.index];
								if (!row) return null;
								return (
									<div
										key={virtualRow.key}
										ref={virtualizer.measureElement}
										data-index={virtualRow.index}
										className="absolute left-0 top-0 w-full pb-2"
										style={{ transform: `translateY(${virtualRow.start}px)` }}
									>
										{row.kind === "header" && (
											<GroupHeader label={t(`groups.${row.key}`)} count={row.count} />
										)}
										{row.kind === "cluster" && (
											<AggregatedGroup
												title={row.items[0]?.title ?? ""}
												items={row.items}
												onMarkRead={(id) => void onMarkRead(id)}
												onDelete={(id) => void onDelete(id)}
												onSelect={onItemSelect}
											/>
										)}
										{row.kind === "item" && (
											<NotificationItem
												item={row.item}
												onMarkRead={(id) => void onMarkRead(id)}
												onDelete={(id) => void onDelete(id)}
												onSelect={onItemSelect}
											/>
										)}
									</div>
								);
							})}
						</div>
					) : null}

					{!showLoading && !showError && !showEmpty && !useVirtual ? (
						<div className="space-y-5">
							{sections.map((section) => (
								<section key={section.key} className="space-y-2">
									<GroupHeader
										label={t(`groups.${section.key}`)}
										count={countNotifications(section.rows)}
									/>
									<div className="space-y-2">
										{section.rows.map((row) =>
											row.kind === "cluster" ? (
												<AggregatedGroup
													key={row.id}
													title={row.items[0]?.title ?? ""}
													items={row.items}
													onMarkRead={(id) => void onMarkRead(id)}
													onDelete={(id) => void onDelete(id)}
													onSelect={onItemSelect}
												/>
											) : (
												<NotificationItem
													key={row.item.id}
													item={row.item}
													onMarkRead={(id) => void onMarkRead(id)}
													onDelete={(id) => void onDelete(id)}
													onSelect={onItemSelect}
												/>
											),
										)}
									</div>
								</section>
							))}
						</div>
					) : null}
				</div>
			</ScrollArea>
		</div>
	);
}

/** Заголовок временной секции — uppercase-метка + счётчик, линия-разделитель. */
function GroupHeader({ label, count }: { label: string; count: number }) {
	return (
		<div className="flex items-center gap-2 px-0.5 pb-2">
			<span className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
				{label}
			</span>
			<span className="text-2xs tabular-nums text-muted-foreground/60">{count}</span>
			<span aria-hidden className="h-px flex-1 bg-border" />
		</div>
	);
}

function matchFilter(type: string, filter: NotificationFilter): boolean {
	if (isNotificationHiddenOnSurface(type)) return false;
	if (filter === "ai") return type.startsWith("ai_") || /Ready|note:|flashcards:|quiz:/i.test(type);
	if (filter === "billing")
		return type.includes("subscription") || type.includes("billing") || type.includes("quota");
	if (filter === "streak") return type.includes("streak") || type.includes("srs");
	if (filter === "system")
		return type.includes("system") || type.includes("security") || type.includes("access");
	return true;
}

export type { DateGroupKey };
